class MobileMenu extends HTMLElement {
  connectedCallback() {
    this.hamburger = document.querySelector('[btn-mobile][data-type="hamburger"]');
    this.closeBtns = this.querySelectorAll('[btn-mobile][data-type="close"]');
    this.countryBtns = this.querySelectorAll('[btn-country]');

    this.rail = this.querySelector('[data-mm="rail"]');
    this.history = [{ id: 'mm-root', scrollTop: 0 }];
    this._prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Cache bound handlers so disconnectedCallback can remove them — otherwise theme-editor
    // re-renders leave stale listeners that fire close() once per orphaned instance.
    this._onToggle = () => this.toggle();
    this._onCloseEvt = () => this.close();
    this._onKeydown = (e) => {
      if (e.key !== 'Escape') return;
      // While the search overlay is layered on top, let it consume the first Escape.
      if (window.Alpine?.store?.('isSearchOpen')) return;
      this.close();
    };

    window.addEventListener('header-navigation:mobile-toggle', this._onToggle);
    window.addEventListener('header-navigation:mobile-close', this._onCloseEvt);

    this.closeBtns.forEach((button) => button.addEventListener('click', () => this._handleClose()));
    this.countryBtns.forEach((button) => button.addEventListener('click', () => window.countryPopup()));

    document.addEventListener('keydown', this._onKeydown);

    if (window.Shopify && window.Shopify.designMode) {
      // block:select fires on the promo block nested inside this menu (this.contains), while
      // section:select fires on the host section that contains this menu (e.target.contains(this)).
      this._onEditorSelect = (e) => { if (this.contains(e.target) || e.target.contains(this)) this.open(); };
      this._onEditorDeselect = (e) => { if (this.contains(e.target) || e.target.contains(this)) this.close(); };
      document.addEventListener('shopify:block:select', this._onEditorSelect);
      document.addEventListener('shopify:block:deselect', this._onEditorDeselect);
      document.addEventListener('shopify:section:select', this._onEditorSelect);
      document.addEventListener('shopify:section:deselect', this._onEditorDeselect);
    }

    this.addEventListener('click', (e) => {
      const drillBtn = e.target.closest('[aria-controls^="mm-root"]');
      if (drillBtn && this.contains(drillBtn)) {
        const targetId = drillBtn.getAttribute('aria-controls');
        // Ignore taps on a button whose panel is already in view.
        const top = this.history && this.history.at(-1);
        if (targetId && top && targetId !== top.id) {
          this._drill(targetId);
        }
        return;
      }
      const backBtn = e.target.closest('[data-type="back"]');
      if (backBtn && this.contains(backBtn)) {
        this._back();
      }
      const accordionBtn = e.target.closest('[data-mm="accordion-toggle"]');
      if (accordionBtn && this.contains(accordionBtn)) {
        this._toggleAccordion(accordionBtn);
      }
    });

    // Theme-editor re-renders can re-fire connectedCallback with stale rail state, so reset to level 1.
    this._snapToL1();
  }

  disconnectedCallback() {
    if (this._onToggle) {
      window.removeEventListener('header-navigation:mobile-toggle', this._onToggle);
    }
    if (this._onCloseEvt) {
      window.removeEventListener('header-navigation:mobile-close', this._onCloseEvt);
    }
    if (this._onKeydown) {
      document.removeEventListener('keydown', this._onKeydown);
    }
    if (this._onEditorSelect) {
      document.removeEventListener('shopify:block:select', this._onEditorSelect);
      document.removeEventListener('shopify:section:select', this._onEditorSelect);
    }
    if (this._onEditorDeselect) {
      document.removeEventListener('shopify:block:deselect', this._onEditorDeselect);
      document.removeEventListener('shopify:section:deselect', this._onEditorDeselect);
    }
  }

  open() {
    this.setAttribute('open', '');
    this._syncAria(true);
    document.querySelector('department-buttons')?.setActiveDepartment();
    this.noScroll(true);
  }

  // The menu's close control closes the search overlay first while it is layered on top,
  // mirroring the Escape coordination — search closes, the menu stays mounted, a second
  // tap then closes the menu.
  _handleClose() {
    if (window.Alpine?.store?.('isSearchOpen')) {
      window.dispatchEvent(new CustomEvent('search:close'));
      return;
    }
    this.close();
  }

  close() {
    // Capture the current panel id before _snapToL1 clears history.
    const fromId = this.history && this.history.at(-1) ? this.history.at(-1).id : 'mm-root';

    this.removeAttribute('open');
    this._syncAria(false);
    this.noScroll(false);

    this._snapToL1();

    this.dispatchEvent(
      new CustomEvent('mobile-menu:close', {
        bubbles: true,
        detail: { fromId },
      }),
    );
  }

  toggle() {
    this.hasAttribute('open') ? this.close() : this.open();
  }

  _syncAria(open) {
    this.hamburger?.setAttribute('aria-expanded', String(open));
    this.closeBtns.forEach((button) => button.setAttribute('aria-expanded', String(open)));
  }

  // Reset the rail to level 1 with no animation and collapse every accordion. Runs while
  // the menu is hidden (on close) or before the user interacts (on connect), so it's never seen.
  _snapToL1() {
    this.history = [{ id: 'mm-root', scrollTop: 0 }];
    this._inflight = false;

    if (!this.rail) return;

    this.rail.style.transition = 'none';
    this.rail.style.transform = '';

    // getElementById, not querySelector — panel ids derive from merchant-editable titles
    // and may not be valid CSS selectors.
    const root = document.getElementById('mm-root');
    if (root && root.parentElement !== this.rail) {
      this.rail.prepend(root);
    }
    // #mm-root is the only active panel after a reset; set it before the loop clears the rest
    // so no stale data-mm-active survives across close -> reopen.
    if (root) root.setAttribute('data-mm-active', '');

    // Collect first, then move: moving a panel out of the rail must not depend on live
    // NodeList state, and keeps the loop free of mid-iteration re-parenting side effects.
    const railPanels = [];
    this.querySelectorAll('[data-mm="panel"]').forEach((panel) => {
      panel.hidden = true;
      panel.removeAttribute('data-mm-active');
      if (panel.parentElement === this.rail) {
        railPanels.push(panel);
      }
    });
    railPanels.forEach((panel) => this.appendChild(panel));

    // The teleport target (#mm-search-slot) survives a snap only because it currently lives
    // inside #mm-root (re-prepended to the rail above), not inside a [data-mm="panel"] that the
    // loop just moved out. A future nesting change that placed the slot inside a panel would
    // detach it here and break in-menu search with no error. Surface that loudly rather than
    // silently mis-restoring it to the wrong position.
    const searchSlot = document.getElementById('mm-search-slot');
    if (searchSlot && !this.rail.contains(searchSlot)) {
      console.error('mobile-menu: #mm-search-slot left the rail during snap — in-menu search teleport target is detached.');
    }

    this.querySelectorAll('[aria-controls^="mm-root"]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });

    this.querySelectorAll('[data-mm="accordion-panel"]').forEach((p) => {
      p.dataset.open = 'false';
    });
    this.querySelectorAll('[data-mm="accordion-toggle"]').forEach((b) => {
      b.setAttribute('aria-expanded', 'false');
    });

    window.requestAnimationFrame(() => {
      this.rail.style.transition = '';
    });
  }

  // Drill into a child panel: a two-panel sliding-window animation.
  _drill(targetPanelId) {
    if (!this.rail) return;
    // Ignore taps while a transition is in progress — a second drill would re-write the same
    // transform, so transitionend would never fire and the source panel would never be moved out.
    if (this._inflight) return;
    const sourcePanel = document.getElementById(this.history.at(-1).id);
    if (!sourcePanel) return;
    const destPanel = document.getElementById(targetPanelId);
    if (!destPanel) return;

    this._inflight = true;

    // Save the scroll position before changing any DOM or styles.
    this.history.at(-1).scrollTop = sourcePanel.scrollTop;

    this.history.push({ id: targetPanelId, scrollTop: 0 });

    destPanel.hidden = false;
    // Clear content-visibility on the entering panel synchronously (before focus could land)
    // so the active panel is tab-reachable; off-screen siblings stay render-skipped.
    destPanel.setAttribute('data-mm-active', '');
    sourcePanel.after(destPanel);

    this.rail.dataset.direction = 'drill';

    // Move the source panel out of the rail and reset the transform once the slide ends.
    // The _inflight flag makes this safe to call more than once, so stray transitionend events do nothing.
    const finish = () => {
      if (!this._inflight) return;
      this.rail.style.transition = 'none';
      sourcePanel.remove();
      this.appendChild(sourcePanel);
      sourcePanel.hidden = true;
      sourcePanel.removeAttribute('data-mm-active');
      this.rail.style.transform = '';
      // Force a reflow so the cleared transform commits before transitions are re-enabled.
      void this.rail.offsetWidth;
      this.rail.style.transition = '';
      this._inflight = false;
    };

    // Wait one animation frame so the reordered panels paint before the transform animates;
    // otherwise some browsers batch both and skip the transition.
    window.requestAnimationFrame(() => {
      this.rail.style.transform = 'translateX(-100%)';
      // Under reduced motion the transition is 0ms and transitionend won't fire reliably,
      // so run cleanup synchronously after the transform write.
      if (this._prefersReducedMotion.matches) finish();
    });

    // If the OS reduce-motion preference flips mid-session the CSS jumps to 0ms but
    // matchMedia lags, so transitionend may never arrive; the timeout guarantees cleanup.
    if (!this._prefersReducedMotion.matches) {
      this.rail.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400);
    }

    // CSS.escape — the id may contain characters that are invalid inside a selector.
    const drillBtn = this.querySelector('[aria-controls="' + CSS.escape(targetPanelId) + '"]');
    if (drillBtn) drillBtn.setAttribute('aria-expanded', 'true');

    this.dispatchEvent(
      new CustomEvent('mobile-menu:drill', {
        bubbles: true,
        detail: { fromId: sourcePanel.id, toId: targetPanelId },
      }),
    );
  }

  // Pop the top panel off the history stack and animate the rail back.
  _back() {
    if (!this.rail) return;
    if (this.history.length <= 1) return;
    if (this._inflight) return;

    const current = this.history.pop();
    const previous = this.history.at(-1);
    const sourcePanel = document.getElementById(current.id);
    const destPanel = document.getElementById(previous.id);
    if (!sourcePanel || !destPanel) {
      // Restore the popped entry so the stack matches the visible rail.
      this.history.push(current);
      return;
    }

    this._inflight = true;

    // Restore scroll before unhiding so the first painted frame is correct.
    destPanel.scrollTop = previous.scrollTop;
    destPanel.hidden = false;
    // Clear content-visibility on the entering panel synchronously (before focus could land).
    destPanel.setAttribute('data-mm-active', '');

    this.rail.prepend(destPanel);

    // Collapse the accordions in the level-2 panel being left (sourcePanel, not destPanel)
    // so it shows them collapsed when re-entered later. Must run before the transform is
    // written so it lands in the same frame as the slide and avoids a flash of an open accordion.
    sourcePanel.querySelectorAll('[data-mm="accordion-panel"]').forEach((p) => {
      p.dataset.open = 'false';
    });
    sourcePanel.querySelectorAll('[data-mm="accordion-toggle"]').forEach((b) => {
      b.setAttribute('aria-expanded', 'false');
    });

    this.rail.dataset.direction = 'back';

    // Jump to -100% with transitions off so the starting position matches the new panel
    // order (the source panel now sits second), then re-enable transitions before animating back to 0.
    this.rail.style.transition = 'none';
    this.rail.style.transform = 'translateX(-100%)';
    void this.rail.offsetWidth;
    this.rail.style.transition = '';

    // Defined before the animation-frame callback so the reduced-motion path can call it.
    // Safe to call more than once (guarded by _inflight).
    const finish = () => {
      if (!this._inflight) return;
      sourcePanel.hidden = true;
      sourcePanel.removeAttribute('data-mm-active');
      if (sourcePanel.parentElement === this.rail) {
        this.appendChild(sourcePanel);
      }
      this._inflight = false;
    };

    // Call finish() only after the transform write is queued (mirrors _drill); otherwise
    // the source panel is moved out before the rail animates.
    window.requestAnimationFrame(() => {
      this.rail.style.transform = '';
      if (this._prefersReducedMotion.matches) finish();
    });
    if (!this._prefersReducedMotion.matches) {
      this.rail.addEventListener('transitionend', finish, { once: true });
      // Fallback for the mid-session reduce-motion toggle (CSS 0ms while matchMedia lags).
      setTimeout(finish, 350);
    }

    const drillBtn = this.querySelector('[aria-controls="' + CSS.escape(current.id) + '"]');
    if (drillBtn) drillBtn.setAttribute('aria-expanded', 'false');

    this.dispatchEvent(
      new CustomEvent('mobile-menu:back', {
        bubbles: true,
        detail: { fromId: current.id, toId: previous.id },
      }),
    );
  }

  // Toggle a level-3 accordion, allowing only one open at a time within its level-2 panel.
  _toggleAccordion(button) {
    const accordionId = button.getAttribute('aria-controls');
    if (!accordionId) return;
    const panel = document.getElementById(accordionId);
    if (!panel) return;

    const wasOpen = panel.dataset.open === 'true';
    const willOpen = !wasOpen;

    // Only one open at a time: collapse any other open accordion in the same level-2 panel first.
    if (willOpen) {
      const l2Panel = button.closest('[data-mm="panel"]');
      if (l2Panel) {
        l2Panel.querySelectorAll('[data-mm="accordion-panel"][data-open="true"]').forEach((openPanel) => {
          if (openPanel === panel) return;
          openPanel.dataset.open = 'false';
          const sibBtn = this.querySelector(
            '[aria-controls="' + CSS.escape(openPanel.id) + '"][data-mm="accordion-toggle"]',
          );
          if (sibBtn) sibBtn.setAttribute('aria-expanded', 'false');
          // The reason field tells listeners this collapse was automatic, not a user tap.
          this.dispatchEvent(
            new CustomEvent('mobile-menu:collapse', {
              bubbles: true,
              detail: {
                panelId: l2Panel.id,
                l2RowId: l2Panel.id,
                accordionId: openPanel.id,
                reason: 'single-open',
              },
            }),
          );
        });
      }
    }

    panel.dataset.open = String(willOpen);
    button.setAttribute('aria-expanded', String(willOpen));

    const l2 = button.closest('[data-mm="panel"]');
    const l2Id = l2 ? l2.id : 'mm-root';
    this.dispatchEvent(
      new CustomEvent(willOpen ? 'mobile-menu:expand' : 'mobile-menu:collapse', {
        bubbles: true,
        detail: {
          panelId: l2Id,
          l2RowId: l2Id,
          accordionId: accordionId,
          reason: 'user',
        },
      }),
    );
  }

  // Defers the scroll lock to after the open/close paint (rAF-gated via the theme's
  // waitForPaint primitive) so the heavy setup never blocks the menu's first frame and the
  // interaction stays responsive. setTimeout is only the last-resort fallback for pages
  // (404/password/checkout) where the theme helpers haven't loaded.
  noScroll(enable) {
    const apply = () => {
      // enableScrollLock/disableScrollLock live in cart.js, which some pages (404, password,
      // checkout) don't load — guard before calling.
      if (enable) {
        if (typeof window.enableScrollLock === 'function') {
          window.enableScrollLock(this);
        }
      } else {
        if (typeof window.disableScrollLock === 'function') {
          window.disableScrollLock(this);
        }
      }

      this.toggleVideos(!enable);
      this.toggleThirdPartyElements(enable);
    };

    if (typeof window.waitForPaint === 'function') {
      window.waitForPaint().then(apply);
    } else if (typeof window.yieldBefore === 'function') {
      window.yieldBefore(apply);
    } else {
      setTimeout(apply, 0);
    }
  }

  toggleVideos(play) {
    // Video helpers also live in cart.js — same conditional-load guard.
    if (play) {
      if (typeof window.continuePlayingVideos === 'function') {
        window.continuePlayingVideos();
      }
    } else {
      if (typeof window.pauseVideosPlaying === 'function') {
        window.pauseVideosPlaying();
      }
    }
  }

  toggleThirdPartyElements(hide) {
    if (hide) {
      document.body.classList.add('hide-third-party-elements');

      return;
    }

    document.body.classList.remove('hide-third-party-elements');
  }
}

customElements.define('mobile-menu', MobileMenu);
